// ForgotPassword.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { authService } from '@services/authService';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

import loginbg from '../../assets/images/loginbg.png';

const ForgotPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await authService.forgotPassword(data.email);
      setEmail(data.email);
      setIsSuccess(true);
      toast.success('Password reset link sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
      toast.success('Reset link sent again');
    } catch (error) {
      toast.error('Failed to resend reset link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        backgroundImage: `url(${loginbg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: '3rem 1rem'
      }}
    >
      <motion.div
        style={{
          maxWidth: '28rem',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Back to Login */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Link
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'var(--primary-600)',
              textDecoration: 'none',
              transition: 'color var(--transition-fast)'
            }}
            onMouseEnter={(e) => (e.target.style.color = 'var(--primary-500)')}
            onMouseLeave={(e) => (e.target.style.color = 'var(--primary-600)')}
          >
            <ArrowLeft style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
                        Back to Login
          </Link>
        </motion.div>

        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <motion.div
            style={{
              margin: '0 auto 1.5rem',
              height: '5rem',
              width: '5rem',
              backgroundColor: isSuccess ? 'var(--success-600)' : 'var(--primary-600)',
              borderRadius: 'var(--radius-lg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            {isSuccess ? (
              <CheckCircle style={{ height: '2.5rem', width: '2.5rem', color: 'white' }} />
            ) : (
              <Mail style={{ height: '2.5rem', width: '2.5rem', color: 'white' }} />
            )}
          </motion.div>

          <h2
            style={{
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--gray-900)',
              marginBottom: '0.5rem'
            }}
          >
            {isSuccess ? 'Check Your Email' : 'Forgot Password?'}
          </h2>

          <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
            {isSuccess
              ? `We've sent a password reset link to ${email}`
              : "No worries, we'll send you reset instructions"}
          </p>
        </div>

        {/* Card */}
        <motion.div
          style={{
            backgroundColor: 'white',
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            padding: '2rem'
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          {!isSuccess ? (
            <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--gray-700)',
                    marginBottom: '0.5rem'
                  }}
                >
                                    Email Address
                </label>

                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '0.75rem',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <Mail style={{ height: '1.25rem', width: '1.25rem', color: 'var(--gray-400)' }} />
                  </div>

                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    style={{
                      width: '100%',
                      paddingLeft: '2.5rem',
                      paddingRight: '0.75rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.75rem',
                      border: errors.email ? '1px solid var(--error-300)' : '1px solid var(--gray-300)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      transition: 'all var(--transition-fast)',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = errors.email ? 'var(--error-500)' : 'var(--primary-500)';
                      e.target.style.boxShadow = errors.email
                        ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                        : '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = errors.email ? 'var(--error-300)' : 'var(--gray-300)';
                      e.target.style.boxShadow = 'none';
                    }}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Invalid email address'
                      }
                    })}
                  />
                </div>

                {errors.email && (
                  <motion.p
                    style={{
                      marginTop: '0.25rem',
                      fontSize: '0.875rem',
                      color: 'var(--error-600)'
                    }}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {errors.email.message}
                  </motion.p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                style={{
                  backgroundColor: 'var(--primary-600)',
                  color: 'white',
                  padding: '0.75rem 1.5rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'background-color var(--transition-fast)',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}
                size="lg"
                disabled={isLoading}
                leftIcon={isLoading ? <Loading size="sm" /> : <Send style={{ width: '1.25rem', height: '1.25rem' }} />}
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                                    We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
                </p>
                <div style={{ padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem' }}>
                  <p style={{ fontSize: '0.875rem', color: '#1e3a8a' }}>
                    <strong>Didn't receive the email?</strong> Check your spam folder or try resending the link.
                  </p>
                </div>
              </div>

              <Button
                onClick={handleResend}
                variant="outline"
                className="w-full"
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'background-color var(--transition-fast)'
                }}
                size="lg"
                disabled={isLoading}
                leftIcon={isLoading ? <Loading size="sm" /> : <Send style={{ width: '1.25rem', height: '1.25rem' }} />}
              >
                {isLoading ? 'Resending...' : 'Resend Link'}
              </Button>

              <div style={{ paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <Link
                  to="/login"
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--primary-600)',
                    textDecoration: 'none',
                    transition: 'color var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => (e.target.style.color = 'var(--primary-500)')}
                  onMouseLeave={(e) => (e.target.style.color = 'var(--primary-600)')}
                >
                                    Back to Login
                </Link>
              </div>
            </div>
          )}
        </motion.div>

        {/* Footer / help */}
        <motion.div
          style={{
            textAlign: 'center',
            fontSize: '0.875rem',
            color: 'var(--gray-600)'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          {!isSuccess && (
            <p>
                            Remember your password?{' '}
              <Link
                to="/login"
                style={{
                  fontWeight: 500,
                  color: 'var(--primary-600)',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => (e.target.style.color = 'var(--primary-500)')}
                onMouseLeave={(e) => (e.target.style.color = 'var(--primary-600)')}
              >
                                Sign in here
              </Link>
            </p>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
